// Persistent outbox for bonus photos awaiting upload (IndexedDB).
//
// The bonus record itself saves instantly (Request A in Bonus.jsx). The
// captured photo is written here BEFORE the background upload starts, so
// it lives on the device — not just in page memory — and survives page
// reloads, tab kills, and offline periods. Leftover photos are re-queued
// when the Bonus page next mounts or the connection returns.
//
// Every operation is best-effort: if IndexedDB is unavailable (private
// browsing, storage pressure) callers still proceed with the in-memory
// upload — only the crash-recovery guarantee is lost.

const DB_NAME = 'bonusPhotoOutbox';
const DB_VERSION = 1;
const STORE = 'pendingPhotos';

function openDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'bonusId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore(mode, operation) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = operation(tx.objectStore(STORE));
        tx.oncomplete = () => {
          db.close();
          resolve(request.result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error);
        };
      })
  );
}

// entry: { bonusId, phone, displayName, blob }
export function addPendingPhoto(entry) {
  return withStore('readwrite', (store) =>
    store.put({ ...entry, queuedAt: Date.now() })
  );
}

export function removePendingPhoto(bonusId) {
  return withStore('readwrite', (store) => store.delete(bonusId)).catch(
    () => {}
  );
}

export function listPendingPhotos() {
  return withStore('readonly', (store) => store.getAll()).catch(() => []);
}
